"use client";

import React, { useEffect, useState } from "react";
import {
  CheckCircle2,
  Loader2,
  PlugZap,
  ShieldCheck,
  X,
} from "lucide-react";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

import ModalDropiIntegration from "./Modals/ModalDropiIntegration";
import ModalMercadoPagoIntegration from "./Modals/ModalMercadoPagoIntegration";
import ModalWhatsAppIntegration from "./Modals/ModalWhatsAppIntegration";
import ModalMetaIntegration from "./Modals/ModalMetaIntegration";
import ModalConfioIntegration from "./Modals/ModalConfioIntegration";
import { disconnectConfioIntegration } from "../../services/confio/confioService";
import ModalSuccessIntegration from "./Modals/ModalSuccessIntegration";
import { INTEGRATIONS_UI_CONFIG } from "./config/integrations.config";

import { postDropiIntegration } from "../../services/integrations/integrationsService";
import { disconnectMetaIntegration } from "../../services/meta/metaService";
import { whatsappOAuthCallback } from "../../services/whatsapp/whatsappService";
import { EmbeddedSignupResult } from "../../hooks/useFacebookSDK";
import { decodeTokenService } from "../../services/decodeTokenService";
import {
  postRegisterSellerApi,
  registerSellerList,
  updatePaymentMethodActive,
  updatePaymentMethodInactive,
} from "../../services/payment/payment";

import { clearIntegrationsSnapshot, useIntegrations } from "./hooks/useIntegrations";
import { IntegrationType } from "./config/integrations.config";
import { handleApi } from "../../utils/handleApi";
import { cn } from "../../lib/utils";
import { clearCachedRequest } from "../../utils/requestCache";

interface IntegrationsPanelProps {
  variant?: "page" | "settings";
}

type IntegrationViewModel = {
  type: IntegrationType;
  name: string;
  logoSrc: string;
  isActive: boolean;
  isConnected: boolean;
  alreadyConfigured?: boolean;
};

const canDisconnectIntegration = (type: IntegrationType) =>
  ["dropi", "mercadopago", "stripe", "meta", "confio"].includes(type);

const copy = {
  title: {
    [Language.SPANISH]: "Integraciones",
    [Language.ENGLISH]: "Integrations",
    [Language.PORTUGUESE]: "Integrações",
  },
  subtitle: {
    [Language.SPANISH]: "Conecta los canales que Fluxi usa para cobrar, entregar, conversar y medir ventas.",
    [Language.ENGLISH]: "Connect the channels Fluxi uses to charge, fulfill, chat, and measure sales.",
    [Language.PORTUGUESE]: "Conecte os canais que o Fluxi usa para cobrar, entregar, conversar e medir vendas.",
  },
  active: {
    [Language.SPANISH]: "Activo",
    [Language.ENGLISH]: "Active",
    [Language.PORTUGUESE]: "Ativo",
  },
  connect: {
    [Language.SPANISH]: "Conectar",
    [Language.ENGLISH]: "Connect",
    [Language.PORTUGUESE]: "Conectar",
  },
  disconnect: {
    [Language.SPANISH]: "Desconectar",
    [Language.ENGLISH]: "Disconnect",
    [Language.PORTUGUESE]: "Desconectar",
  },
  configured: {
    [Language.SPANISH]: "Configurado",
    [Language.ENGLISH]: "Configured",
    [Language.PORTUGUESE]: "Configurado",
  },
};

const integrationDescriptions: Record<IntegrationType, Record<Language, string>> = {
  dropi: {
    [Language.SPANISH]: "Automatiza pedidos y fulfillment con tu cuenta de Dropi.",
    [Language.ENGLISH]: "Automate orders and fulfillment with your Dropi account.",
    [Language.PORTUGUESE]: "Automatize pedidos e fulfillment com sua conta Dropi.",
  },
  mercadopago: {
    [Language.SPANISH]: "Recibe pagos locales con Mercado Pago en tus checkouts.",
    [Language.ENGLISH]: "Receive local payments with Mercado Pago in your checkouts.",
    [Language.PORTUGUESE]: "Receba pagamentos locais com Mercado Pago nos seus checkouts.",
  },
  stripe: {
    [Language.SPANISH]: "Activa pagos con tarjeta y checkout internacional con Stripe.",
    [Language.ENGLISH]: "Enable card payments and international checkout with Stripe.",
    [Language.PORTUGUESE]: "Ative pagamentos com cartão e checkout internacional com Stripe.",
  },
  shopify: {
    [Language.SPANISH]: "Sincroniza productos y pedidos con Shopify cuando esté disponible.",
    [Language.ENGLISH]: "Sync products and orders with Shopify when available.",
    [Language.PORTUGUESE]: "Sincronize produtos e pedidos com Shopify quando disponível.",
  },
  whatsapp: {
    [Language.SPANISH]: "Conecta WhatsApp Business para chat, confirmaciones y atención.",
    [Language.ENGLISH]: "Connect WhatsApp Business for chat, confirmations, and support.",
    [Language.PORTUGUESE]: "Conecte WhatsApp Business para chat, confirmações e atendimento.",
  },
  meta: {
    [Language.SPANISH]: "Conecta Meta Ads para campañas, cuentas publicitarias y medición.",
    [Language.ENGLISH]: "Connect Meta Ads for campaigns, ad accounts, and measurement.",
    [Language.PORTUGUESE]: "Conecte Meta Ads para campanhas, contas de anúncio e medição.",
  },
  confio: {
    [Language.SPANISH]: "Habilita pagos y validaciones con Confío cuando aplica para tu país.",
    [Language.ENGLISH]: "Enable payments and validations with Confío when available for your country.",
    [Language.PORTUGUESE]: "Ative pagamentos e validações com Confío quando disponível no seu país.",
  },
};

const IntegrationStatus = ({
  integration,
  language,
}: {
  integration: IntegrationViewModel;
  language: Language;
}) => {
  if (!integration.isActive) return null;

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
      <CheckCircle2 className="h-3.5 w-3.5" />
      {copy.active[language]}
    </span>
  );
};

const IntegrationLogo = ({ integration }: { integration: IntegrationViewModel }) => {
  const imageClass = {
    dropi: "h-7 w-7 object-contain",
    mercadopago: "h-7 w-7 object-contain",
    meta: "h-9 w-9 object-contain",
    stripe: "h-8 w-[74px] max-w-none object-cover",
    confio: "h-7 w-7 object-contain",
    shopify: "h-7 w-7 object-contain",
    whatsapp: "h-8 w-8 object-contain",
  } satisfies Record<IntegrationType, string>;

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
      <img
        src={integration.logoSrc}
        alt={integration.name}
        className={cn("shrink-0", imageClass[integration.type])}
      />
    </div>
  );
};

const IntegrationRow = ({
  integration,
  language,
  loading,
  onConnect,
  onCancel,
}: {
  integration: IntegrationViewModel;
  language: Language;
  loading: boolean;
  onConnect: () => void;
  onCancel: () => void;
}) => {
  const canDisconnect = canDisconnectIntegration(integration.type);
  const description =
    integrationDescriptions[integration.type]?.[language] ??
    integrationDescriptions[integration.type]?.[Language.SPANISH] ??
    "";

  return (
    <article className="group grid gap-3 border-b border-gray-100 py-4 last:border-b-0 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <IntegrationLogo integration={integration} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold text-gray-950">{integration.name}</h3>
            <IntegrationStatus integration={integration} language={language} />
          </div>
          <p className="mt-0.5 max-w-[520px] text-sm leading-5 text-gray-500">
            {description}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 md:justify-self-end">
        {integration.isConnected && !canDisconnect ? (
          <span className="inline-flex h-9 min-w-[118px] items-center justify-center gap-2 rounded-xl bg-white px-3 text-sm font-semibold text-gray-500 ring-1 ring-gray-200">
            <ShieldCheck className="h-4 w-4" />
            {copy.configured[language]}
          </span>
        ) : integration.isConnected ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="inline-flex h-9 min-w-[118px] items-center justify-center gap-2 rounded-xl bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            {copy.disconnect[language]}
          </button>
        ) : (
          <button
            type="button"
            onClick={onConnect}
            disabled={loading}
            className="inline-flex h-9 min-w-[118px] items-center justify-center gap-2 rounded-xl bg-gray-950 px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            {copy.connect[language]}
          </button>
        )}
      </div>
    </article>
  );
};

const IntegrationsPanelSkeleton = ({ language }: { language: Language }) => (
  <div>
    <header className="mb-4">
      <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
        {copy.title[language]}
      </h1>
    </header>

    <div className="border-y border-gray-100">
      {[0, 1, 2, 3, 4].map((item) => (
        <div
          key={item}
          className="grid min-h-[76px] grid-cols-[48px_minmax(0,1fr)_auto] items-center gap-3 border-b border-gray-100 py-3 last:border-b-0"
        >
          <div className="h-11 w-11 animate-pulse rounded-xl bg-gray-100 ring-1 ring-gray-200" />
          <div className="min-w-0 pr-2">
            <div className="mb-2 flex items-center gap-2">
              <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
              <div className="h-5 w-14 animate-pulse rounded-full bg-gray-100" />
            </div>
            <div className="h-3.5 w-full max-w-[420px] animate-pulse rounded bg-gray-100" />
            <div className="mt-1.5 h-3.5 w-2/3 max-w-[300px] animate-pulse rounded bg-gray-100" />
          </div>
          <div className="h-9 w-[118px] animate-pulse rounded-xl bg-gray-100" />
        </div>
      ))}
    </div>
  </div>
);

const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({
  variant = "page",
}) => {
  const { language, country } = useAuth();
  const { integrations: initialIntegrations, loading } = useIntegrations(country);
  const [integrations, setIntegrations] = useState<IntegrationViewModel[]>([]);

  useEffect(() => {
    setIntegrations(initialIntegrations as IntegrationViewModel[]);
  }, [initialIntegrations]);

  const [modalOpenDropi, setModalOpenDropi] = useState(false);
  const [modalOpenMercadoPago, setModalOpenMercadoPago] = useState(false);
  const [modalOpenWhatsApp, setModalOpenWhatsApp] = useState(false);
  const [modalOpenMeta, setModalOpenMeta] = useState(false);
  const [modalOpenConfio, setModalOpenConfio] = useState(false);
  const [loadingWhatsApp, setLoadingWhatsApp] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [tokenDropi, setTokenDropi] = useState("");
  const [tokenMercadoPagoPublic, setTokenMercadoPagoPublic] = useState("");
  const [tokenMercadoPagoPrivate, setTokenMercadoPagoPrivate] = useState("");
  const [loadingMercadoPago, setLoadingMercadoPago] = useState(false);

  const [loadingCard, setLoadingCard] = useState(false);

  const [successModal, setSuccessModal] = useState<{ open: boolean; type: string | null }>({ open: false, type: null });

  const updateIntegrationState = (type: string, active: boolean) => {
    clearCachedRequest("integrations:");
    clearIntegrationsSnapshot();
    setIntegrations(prev =>
      prev.map(i =>
        i.type === type ? { ...i, isActive: active, isConnected: active, alreadyConfigured: active } : i
      )
    );
  };

  const handleSuccessClose = () => {
    setSuccessModal({ open: false, type: null });
  };

  const showSuccess = (type: string) => {
    updateIntegrationState(type, true);
    setSuccessModal({ open: true, type });
  };

  const getIntegrationErrorMessage = (error: unknown) => {
    const trigger = (error as { response?: { data?: { trigger?: string } } })?.response?.data?.trigger;
    return trigger && dictionary[trigger]?.[language]
      ? dictionary[trigger][language]
      : dictionary["integration-error"][language];
  };

  const handleConnect = (type: IntegrationType) => {
    if (type === "dropi") setModalOpenDropi(true);
    if (type === "mercadopago") setModalOpenMercadoPago(true);
    if (type === "whatsapp") setModalOpenWhatsApp(true);
    if (type === "meta") setModalOpenMeta(true);
    if (type === "confio") setModalOpenConfio(true);
    if (type === "stripe") handlePostStripeIntegration(integrations.find((integration) => integration.type === "stripe")?.alreadyConfigured ?? false);
  };

  const handleCancel = async (type: IntegrationType) => {
    try {
      if (type === "dropi") {
        await postDropiIntegration({
          is_active: false,
          integration_token: "",
        });
      }

      if (type === "mercadopago") {
        await updatePaymentMethodInactive(registerSellerList.MERCADO_PAGO);
      }

      if (type === "stripe") {
        await updatePaymentMethodInactive(registerSellerList.STRIPE);
      }

      if (type === "meta") {
        await disconnectMetaIntegration();
      }

      if (type === "confio") {
        await disconnectConfioIntegration();
      }

      toast.success(dictionary["integration-canceled"][language]);
      updateIntegrationState(type, false);
    } catch (error: unknown) {
      toast.error(getIntegrationErrorMessage(error));
    }
  };

  const handlePostDropiIntegration = async () => {
    try {
      await postDropiIntegration({
        integration_token: tokenDropi,
        is_active: true,
      });

      setModalOpenDropi(false);
      setTokenDropi("");
      showSuccess("dropi");
    } catch {
      toast.error(dictionary["integration-error"][language]);
    }
  };

  const handlePostMercadoPagoIntegration = async () => {
    const id = registerSellerList.MERCADO_PAGO;
    setLoadingMercadoPago(true);
    try {
      await postRegisterSellerApi(id, {
        public_key: tokenMercadoPagoPublic,
        token: tokenMercadoPagoPrivate,
      });

      await updatePaymentMethodActive("mercadopago");

      setModalOpenMercadoPago(false);
      setTokenMercadoPagoPublic("");
      setTokenMercadoPagoPrivate("");
      showSuccess("mercadopago");
    } catch (error: unknown) {
      toast.error(getIntegrationErrorMessage(error), { duration: 5000 });
    } finally {
      setLoadingMercadoPago(false);
    }
  };

  const handlePostStripeIntegration = async (alreadyConfigured: boolean = false) => {
    setLoadingCard(true);
    if (alreadyConfigured) {
      const {res, error} = await handleApi(updatePaymentMethodActive(registerSellerList.STRIPE), false);
      if (error) {
        toast.error(dictionary["integration-error"][language]);
      }
      if (res) {
        showSuccess("stripe");
      }
      setLoadingCard(false);
      return;
    }
    const { res, error } = await handleApi(
      postRegisterSellerApi(registerSellerList.STRIPE, {}),
      false
    );
    if (error) {
      toast.error(dictionary["integration-error"][language]);
    }
    if (res) {
      if (res.config.onboarding_url) {
        window.open(res.config.onboarding_url, "_blank");
      }
      showSuccess("stripe");
    }
    setLoadingCard(false);
  };

  const handleWhatsAppOAuthComplete = async (data: EmbeddedSignupResult) => {
    setLoadingWhatsApp(true);
    try {
      const ownerId = decodeTokenService.getUserIdFromToken();
      if (!ownerId) throw new Error("No owner_id found");
      await whatsappOAuthCallback({
        code: data.code,
        phone_number_id: data.phone_number_id,
        waba_id: data.waba_id,
        owner_id: ownerId,
      });
      setModalOpenWhatsApp(false);
      showSuccess("whatsapp");
    } catch {
      toast.error(dictionary["integration-error"][language]);
    } finally {
      setLoadingWhatsApp(false);
    }
  };

  const handleMetaOAuthComplete = () => {
    setLoadingMeta(false);
    setModalOpenMeta(false);
    showSuccess("meta");
  };

  if (loading) {
    return (
      <div
        className={cn(
          variant === "page" &&
            "box-border min-h-screen w-full bg-[#fbfcff] px-4 py-8 md:w-[min(calc(100vw-240px),1100px)] md:px-12"
        )}
      >
        <IntegrationsPanelSkeleton language={language} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        variant === "page" &&
          "box-border min-h-screen w-full bg-[#fbfcff] px-4 py-8 md:w-[min(calc(100vw-240px),1100px)] md:px-12"
      )}
    >
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
          {copy.title[language]}
        </h1>
      </header>

      <div className="border-y border-gray-100">
        {(integrations as IntegrationViewModel[]).map((integration) => (
          <IntegrationRow
            key={integration.type}
            integration={integration}
            language={language}
            onConnect={() => handleConnect(integration.type)}
            onCancel={() => handleCancel(integration.type)}
            loading={loadingCard}
          />
        ))}
      </div>

      {modalOpenDropi && (
        <ModalDropiIntegration
          setModalOpenDropi={setModalOpenDropi}
          handlePostDropiIntegration={handlePostDropiIntegration}
          token={tokenDropi}
          setToken={setTokenDropi}
          language={language}
        />
      )}

      {modalOpenMercadoPago && (
        <ModalMercadoPagoIntegration
          id="mercadopago"
          setModalOpenMercadoPago={setModalOpenMercadoPago}
          handlePostMercadoPagoIntegration={handlePostMercadoPagoIntegration}
          tokenPublic={tokenMercadoPagoPublic}
          setTokenPublic={setTokenMercadoPagoPublic}
          tokenPrivate={tokenMercadoPagoPrivate}
          setTokenPrivate={setTokenMercadoPagoPrivate}
          loading={loadingMercadoPago}
          language={language}
        />
      )}

      {modalOpenWhatsApp && (
        <ModalWhatsAppIntegration
          setModalOpen={setModalOpenWhatsApp}
          handleOAuthComplete={handleWhatsAppOAuthComplete}
          loading={loadingWhatsApp}
          language={language}
        />
      )}

      {modalOpenMeta && (
        <ModalMetaIntegration
          setModalOpen={setModalOpenMeta}
          handleOAuthComplete={handleMetaOAuthComplete}
          loading={loadingMeta}
          language={language}
        />
      )}

      {modalOpenConfio && (
        <ModalConfioIntegration
          setModalOpen={setModalOpenConfio}
          language={language}
          onConnected={() => {
            setModalOpenConfio(false);
            showSuccess("confio");
          }}
        />
      )}

      <ModalSuccessIntegration
        isOpen={successModal.open}
        onClose={handleSuccessClose}
        integrationName={successModal.type ? (INTEGRATIONS_UI_CONFIG[successModal.type as keyof typeof INTEGRATIONS_UI_CONFIG]?.name ?? "") : ""}
        integrationLogo={successModal.type ? (INTEGRATIONS_UI_CONFIG[successModal.type as keyof typeof INTEGRATIONS_UI_CONFIG]?.logoSrc ?? "") : ""}
        language={language}
      />
    </div>
  );
};

export default IntegrationsPanel;
